import React, { useEffect, useState } from "react";
import { Tabs, DatePicker, Space, Empty, Radio } from "antd";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/CommonElements/PageTitle";
import { Button, Modal, Row, Stack } from "react-bootstrap";
import { Bus01Icon, Mosque02Icon, PencilEdit01Icon, RealEstate01FreeIcons } from "@hugeicons/core-free-icons/index";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrivalCard } from "../Makkah/Reception/ArrivalCard";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import ActionDropdown from "@/CommonElements/ActionDropdown";
import { useConfirm } from "@/utils/hooks/useConfrim";
import { useForm } from "react-hook-form";
import { Controllers } from "@/CommonElements/Controller";
import { DATE_TIME_FORMAT, DATE_TIME_FORMAT_INPUT, SYSTEMS, TIME_FORMAT } from "@/utils/constants";
import { LabelValue } from "@/Component/Value";
import { AntInput } from "@/CommonElements/AntInput";
import dayjs from "dayjs";
import { filterObject, getDayNameInArabic, handleFormErrors, joinValues, PickKeyPairs } from "@/utils/helpers";
import { useCancelDepartureMutation, useMakkahCampaignOrGroupDepartureListQuery, useMarkDepartureMutation } from "@/services/Departure/departures";
import { useUpdateDepartureMutation } from "@/services/arrivals";
import { Container, Spinner } from "reactstrap";
import { SearchFilter } from "@/CommonElements/SearchFilter/SearchFilter";
import { AntPagination } from "@/Component/AntPagination/AntPagination";
import { useParamFilters } from "@/utils/hooks/useParamFilters";
import { Info } from "@/Component/Info";
import { useSystem } from "@/utils/hooks/useLocation";
import { useLocation } from "react-router-dom";

export const CampaignAndGroupDeparture = () => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const isDepartureGroupsRoute = pathname.includes("departure_groups");
    const { setSearchParams, getParamFilters, getAllParamFilters } = useParamFilters();
  const { currentSystem } = useSystem()
    // Get current source from URL, default to 'nusuk' if not present
    const defaultSource = "-1";
    const currentSource = getParamFilters("direction_id") || defaultSource;

    const handleTabChange = (key) => {
        const allParams = getAllParamFilters() || {};
        setSearchParams(() => ({ ...allParams, direction_id: key }));
    };

    const tabs = [
        {
            key: defaultSource,
            label: t("TO_PORT"),
            icon: <HugeiconsIcon size={14} icon={RealEstate01FreeIcons} />,
        },
        {
            key: currentSystem === SYSTEMS.makkah ? "2" : "1",
            label: <Space >
                {t(currentSystem === SYSTEMS.makkah ? "TO_MADINAH" : "TO_MAKKAH")}</Space>,
            icon: <HugeiconsIcon size={14} icon={Mosque02Icon} />
        },
    ];

    return (
        <Stack gap={3}>
            <PageHeader
                title={isDepartureGroupsRoute ? t("DEPARTURE_GROUPS") : t("CAMPAIGN_DEPARTURE")}
                sticky={false}
            />
            <Container fluid>
                <Stack gap={3}>

                    <Tabs
                        activeKey={currentSource}
                        items={tabs}
                        onChange={handleTabChange}
                        className="mb-3"
                    />

                    <CampaignAndGroupDepartureList isDepartureGroupsRoute={isDepartureGroupsRoute} />
                </Stack>
            </Container>
        </Stack>
    );
};

const CampaignAndGroupDepartureList = ({ isDepartureGroupsRoute = false }) => {
    const { t } = useTranslation();
    const {
        getParamFilters,
        getAllParamFilters,
        setSearchParams
    } = useParamFilters();
    const { getLocalizedDate, getLocalizedText } = useLocalizedText();
    const { confirm, ModalComponent } = useConfirm();
    const [selectedDeparture, setSelectedDeparture] = useState(null);
    const [{ count = 0, ...filters }, setFilters] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const { currentSystem } = useSystem()
    const isMakkah = currentSystem === SYSTEMS.makkah;
    const departureListType = isDepartureGroupsRoute ? "groups" : "campaigns";
    // ================
    // Side Effects
    // ================
    const listParams = {
        page: currentPage,
        per_page: 10,
        ...filters,
    };
    const { data: departureRes, isLoading, isFetching } = useMakkahCampaignOrGroupDepartureListQuery(
        listParams,
        departureListType,
    );
    const loading = isLoading || isFetching;
    const departureData = loading ? [{ arrival_campaign: {}, departure_details: Array.from({ length: 10 }).fill({ loading: true }) }] :
        (departureRes?.data?.data || []);
    const meta = departureRes?.data?.meta || {};

    // Create action buttons for each arrival
    const { mutate: markDeparture, isPending: isMarkingDeparture } = useMarkDepartureMutation({
        listType: departureListType,
    });
    const { mutate: cancelDeparture, isPending: isCancellingDeparture } = useCancelDepartureMutation({
        listType: departureListType,
    });

    const createActions = (arrival) => {
        const createReceptionCardButton = (
            <Button
                className="btn-primary flex-fill"
                onClick={() => {
                    confirm({
                        title: t("CONFIRM"),
                        content: t("CONFIRM_DEPARTURE_MESSAGE"),
                        okText: t("CONFIRM"),
                        cancelText: t("CANCEL"),
                        onOk: ({ closeModal }) => {
                            const payload = [arrival.id]
                            markDeparture({ ids: payload }, {
                                onSuccess: () => {
                                    closeModal()
                                }
                            });

                            return false;
                        }
                    });
                }}
            >
                {t("HAS_BEEN_DEPARTED")}
            </Button>
        );

        const cancelFlightButton = (
            <Button
                className="btn-danger flex-fill"
                onClick={() => {

                    confirm({
                        title: t("CONFIRM"),
                        content: t("CONFIRM_CANCEL_DEPARTURE_MESSAGE"),
                        okText: t("CONFIRM"),
                        cancelText: t("CANCEL"),
                        onOk: ({ closeModal }) => {
                            cancelDeparture({ ids: [arrival.id] }, {
                                onSuccess: () => {
                                    closeModal()
                                }
                            });

                            return false;
                        }
                    });
                }}
                disabled={isCancellingDeparture}
            >
                {t("CANCEL_DEPARTURE")}
            </Button>
        );
        if (!!arrival.departure_at) return [cancelFlightButton];
        return [createReceptionCardButton];
    };

    const labelValues = [
        {
            key: "haj_count",
            label: t("ARRIVAL_CARD_PILGRIMS_COUNT"),
        },
        {
            key: "exit_date",
            label: t("EXIT_DATE"),
            render: (value) => {
                return value ? getLocalizedDate(value, DATE_TIME_FORMAT) : "-"
            },
        },
        {
            key: "means_id",
            label: t("MEANS_OF_TRANSPORT"),
            render: (value) => <SelectCachedOptions.Display
                optionKey="transportation"
                value={value}
                campareBy="id"
                render={(option) => getLocalizedText(option?.name_ar, option?.name_en)}
            />,
        },
        {
            key: "transport_company",
            label: t("TRANSPORT_COMPANY"),
            render: (value, arrival) => (
                <SelectCachedOptions.Display
                    optionKey="buses"
                    value={value}
                    campareBy="company_id"
                    render={(option) => {
                        return (value && <Space>
                            {arrival?.transport_company_name_ar || arrival?.transport_company_name_en ? <Info title={`${t("TRANSPORT_COMPANY")}: ${getLocalizedText(arrival?.transport_company_name_ar, arrival?.transport_company_name_en)} - ${arrival?.transport_contract_no}`} /> : null}
                            {getLocalizedText(arrival?.transport_company_name_ar, arrival?.transport_company_name_en)}
                        </Space>) || "-"
                    }}
                />
            ),
        },
        {
            key: "bus_count",
            label: t("BUSES_COUNT"),
        },
        {
            key: "country_name",
            label: t("ORGANIZER"),
            render: (_, arrival) => getLocalizedText(arrival?.country_name_ar, arrival?.country_name_en) || "-",
        },
        {
            key: "direction_name",
            label: t("DEPARTURE_DIRECTION"),
            render: (value) => value,
        },
        {
            key: "port_id",
            label: t("EXIT_PORT"),
            render: (value) => <SelectCachedOptions.Display
                optionKey="ports"
                value={value}
                campareBy="id"
                render={(option) => getLocalizedText(option?.name_ar, option?.name_en) || option?.name}
            />,
        },
        {
            key: "house_madina_contract_id",
            label: t("BUILDING_CONTRACT_NUMBER"),
            render: (value) => <SelectCachedOptions.Display
                optionKey="madinahContracts"
                value={value}
                campareBy="id"
                render={(option) => <Space>
                    <Info title={option?.house_commercial_name_ar} />
                    {option?.id}
                </Space>}
            />,
        },
        {
            key: "service_center",
            label: isMakkah ? t("SERVICE_CENTER") : t("MADINAH_SERVICE_CENTER_NUMBER"),
            render: (value) => getLocalizedText(value?.name_ar, value?.name_en),
        },
        {
            key: "departure_at", // missing
            label: t("DEPARTURE_AT"),
            render: (value) => value ? getLocalizedDate(value, DATE_TIME_FORMAT) : "-",
        },
        {
            key: "flight_date",
            label: t("FLIGHT_DATE"),
            render: (value) => {
              return value ? getLocalizedDate(value, DATE_TIME_FORMAT) : "-"
            },
          },
    ]

    const createHeaderActions = (arrival) => [
        {
            label: t("EDIT"),
            onClick: () => {
                setSelectedDeparture(arrival)
            },
            disabled: false,
            icon: <HugeiconsIcon size={14} icon={PencilEdit01Icon} />
        },
    ]

    const onCloseDepartureForm = () => {
        setSelectedDeparture(null)
    }

    const handleDebouncedChange = (searchValue) => {
        setFilters(searchValue);
        setCurrentPage(1);
    };

    const filterFields = [
        { name: "campaign_number", label: t("CAMPAIGN_NUMBER"), type: "text" },
        { name: "exit_date", label: t("EXIT_DATE"), type: "date" },
        {
            name: "means_id", label: t("MEANS_OF_TRANSPORT"), type: "select", optionKey: "transportation", options: (transport) => ({
                value: transport.id,
                label: <LabelValue.Value value={[transport.id, transport.name].join(" - ")} />,
            })
        },
        {
            name: "transport_company", label: t("TRANSPORT_COMPANY"), type: "select", optionKey: "buses",
            options: (bus) => ({
                value: bus.company_id,
                label: <LabelValue.Value value={[bus.company_id, bus.company_name].join(" - ")} />,
            })
        },
        {
            name: "organizer", label: t("ORGANIZER"), type: "select", optionKey: "missions", options: (mission) => ({
                value: mission.id,
                label: <LabelValue.Value value={[mission.id, getLocalizedText(mission.name_ar, mission.name_en)].join(" - ")} />,
            })
        },
        {
            name: "service_center", 
            label: t("SERVICE_CENTER"), 
            type: "select", 
            render: (value) => getLocalizedText(value?.name_ar, value?.name_en),
        },
        { name: "departure_at", label: t("DEPARTURE_AT"), type: "date" },
    ]

    const getStatusConfig = (item) => {
        switch (item.status) {
            case "scheduled":
                return ({
                    color: "brand",
                    icon: Bus01Icon,
                    text: <Space>
                        {t("DEPARTURE_CANCELLED")}
                    </Space>,
                })
            case "departed":
                return ({
                    color: "warning",
                    icon: Bus01Icon,
                    text: <Space>
                        {t("DEPARTURE_COMPLETED")}
                    </Space>,
                })
            case "arrived":
                return ({
                    color: "success",
                    icon: Bus01Icon,
                    text: <Space>
                        {t("DEPARTURE_ARRIVED")}
                    </Space>,
                })

            default:
                return ({
                    color: "gray",
                    icon: Bus01Icon,
                    text: <Space>
                        {t("DEPARTURE_SCHEDULED")}
                    </Space>,
                })
        }
    }

    const no_results = departureData?.length === 0;
    const no_filter_results = !departureData?.length && !!filterObject(filters).length;
    const handleDateChange = (e) => {
        const filters = getAllParamFilters()
        setSearchParams({ ...filters, duration: e.target.value })
    }

    return (
        <Stack gap={3}>

            <Radio.Group
                // disabled={loading}
                onChange={handleDateChange}
                value={getParamFilters("duration") || "today"}
                options={[
                    { label: t("TODAY"), value: "today" },
                    { label: t("ALL"), value: "all" },
                ]}
            />
            <SearchFilter filterFields={filterFields} onChange={handleDebouncedChange} debounced />

            {no_filter_results && (
                <Empty
                    image={<HugeiconsIcon size={40} icon={Bus01Icon} />}
                    description={t("NO_FILTER_RESULTS")}
                />
            )}

            {no_results && (
                <Empty
                    image={<HugeiconsIcon size={40} icon={Bus01Icon} />}
                    description={t("NO_DEPARTURES_FOUND")}
                />
            )}

            <Row>
                {departureData?.map((item) => item?.departure_details?.map((departure) => {
                    const arrival = { ...item.arrival_campaign, ...departure }
                    return (
                        <ArrivalCard
                            loading={arrival.loading}
                            arrival={arrival}
                            labelValues={labelValues}
                            title={`${t("ARRIVAL_CARD_CAMPAIGN_NUMBER")}: `}
                            titleValue={arrival.campaign_id || "-"}
                            actions={createActions(arrival)}
                            statusConfig={getStatusConfig(arrival)}
                            renderActions={() => <ActionDropdown
                                removeContainer={true}
                                items={createHeaderActions(arrival)}
                                iconAlign="vertical"
                                buttonClassName="outline btn-icon btn-xs"
                            />
                            }

                        />
                    )
                }))}
            </Row>

            <AntPagination
                className="w-100 p-3 bg-white position-sticky bottom-0"
                hideOnSinglePage
                pageSize={meta?.per_page || 10}
                current={meta?.current_page || 1}
                onChange={() => { }}
                total={meta?.total || 0}
                showSizeChanger={false}
            />

            <ModalComponent loading={isMarkingDeparture || isCancellingDeparture} />
            <DepartureForm
                selectedDeparture={selectedDeparture}
                onClose={onCloseDepartureForm}
                listType={departureListType}
            />
        </Stack>
    );
};

const DepartureForm = (props) => {
    const { selectedDeparture, onClose, listType = "campaigns" } = props;
    const { t } = useTranslation();
    const isOpen = !!selectedDeparture;

    // ================
    // Side Effects
    // ================
    const { mutate: updateDeparture, isPending: updateDeparturePending } = useUpdateDepartureMutation({
        listType,
    });

    const form = useForm({ disabled: updateDeparturePending });
    const { control, formState: { errors }, watch } = form

    const onSave = (data) => {
        const payload = { ...data }
        updateDeparture(payload, {
            onSuccess: () => {
                onClose();
            },
            onError: (error) => {
                handleFormErrors(error, form.setError, t, "DEPARTURE_UPDATE_FAILED");
            }
        });
    };

    useEffect(() => {
        const payload = PickKeyPairs(selectedDeparture, ["exit_date", "exit_day", "transport_company", "bus_count", "haj_count", "id"]);
        form.reset(payload ?? {})
    }, [selectedDeparture])

    const ctrls = [
        {
            labelProps: { children: t("EXIT_DATE"), className: "required" },
            controllerProps: {
                name: "exit_date",
                rules: { required: t("FIELD_REQUIRED") },
                render: ({ field }) => (
                    <DatePicker
                        {...field}
                        size="large"
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(date) => {
                            field.onChange(date);
                            const dayName = getDayNameInArabic(date);
                            form.setValue("exit_day", dayName);
                        }}
                        id="exit_date"
                        className="w-100"
                        placeholder={t("EXIT_DATE")}
                        status={errors.departure_date ? "error" : undefined}
                        format={DATE_TIME_FORMAT_INPUT}
                        showTime={{ format: TIME_FORMAT, minuteStep: 1 }}
                        disabledDate={(current) => current && current < dayjs().startOf("day")}
                    />
                ),
            },
        },
        {
            labelProps: { children: t("DEPARTURE_DAY") },
            controllerProps: { name: "exit_day" },
            isViewMode: true,
            renderValue: (value) => value || "-",
        },
        // {
        //     labelProps: { children: t("TRANSPORT_COMPANY"), for: "transport_company" },
        //     controllerProps: {
        //         name: "transport_company",
        //         render: ({ field }) => (
        //             <SelectCachedOptions
        //                 {...field}
        //                 showSearch
        //                 placeholder={t("TRANSPORT_COMPANY")}
        //                 id="transport_company"
        //                 optionKey="buses"
        //                 options={(bus) => ({
        //                     value: String(bus.company_id),
        //                     label: <LabelValue.Value value={joinValues([
        //                         bus.bus_id ? `(${bus.bus_id})` : "",
        //                         bus.company_name,
        //                     ])} />,
        //                 })}
        //                 status={
        //                     errors?.transport_company ? "error" : undefined
        //                 }
        //             />
        //         ),
        //     },
        // },
        {
            labelProps: { children: t("BUSES_COUNT"), for: "bus_count" },
            controllerProps: {
                name: "bus_count",
                rules: {
                    validate: (value) => {
                        if (!value) return true;
                        if (!/^\d+$/.test(value)) return t("MUST_BE_NUMBERS_DIGITS", { defaultValue: "Must be numbers/digits" });
                        if (value.length > 2) return t("CAMPAIGNS_FORM_MAX_TWO_DIGITS", { defaultValue: t("MAX_TWO_DIGITS", { defaultValue: "Max 2 digits" }) });
                        return true;
                    },
                },
                render: ({ field }) => (
                    <AntInput
                        {...field}
                        id="bus_count"
                        type="text"
                        placeholder={t("BUSES_COUNT")}
                        status={errors.buses_count ? "error" : undefined}
                        maxLength={2}
                    />
                ),
            },
        },
        {
            labelProps: { children: t("PILGRIMS_COUNT"), for: "haj_count" },
            controllerProps: {
                name: "haj_count",
                render: ({ field }) => (
                    <AntInput
                        {...field}
                        showSearch
                        id="haj_count"
                        placeholder={t("PILGRIMS_COUNT")}
                        status={errors.haj_count ? "error" : undefined}
                    />
                ),
            },
        },
    ];

    return (
        <Modal
            show={isOpen}
            onHide={onClose}
            centered
            size="lg"
            backdrop="static"
            keyboard={false}
        >
            <form onSubmit={form.handleSubmit(onSave)}>
                <Modal.Header className="sticky-top bg-white">
                    <span className="h4">{t("EDIT_DEPARTURE")}</span>
                </Modal.Header>
                <Modal.Body>
                    <Controllers
                        controllers={ctrls.map((c) => ({
                            ...c,
                            name: c.controllerProps.name,
                            controllerProps: { ...c.controllerProps, control, errors },
                        }))}
                        watch={watch}
                    />
                </Modal.Body>
                <Modal.Footer className="sticky-bottom bg-white d-flex justify-content-start">
                    <Space className="w-100">
                        <Button
                            disabled={updateDeparturePending}
                            className="btn-sm"
                            variant="primary"
                            type="submit">
                            {updateDeparturePending ? <Spinner size="sm" /> : t("UPDATE")}
                        </Button>
                        <Button
                            disabled={updateDeparturePending}
                            style={{ pointerEvents: updateDeparturePending ? "none" : "auto" }}
                            className="btn-sm"
                            variant="danger"
                            onClick={onClose}>
                            {t("CANCEL")}
                        </Button>
                    </Space>
                </Modal.Footer>
            </form>
        </Modal >
    )
}